import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Sun } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { EmptyState } from '../components/atoms/EmptyState';
import { Grid } from '../components/atoms/Grid/Grid';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { Stack } from '../components/atoms/Stack/Stack';
import { Heading } from '../components/atoms/Typography/Heading';
import { Text } from '../components/atoms/Typography/Text';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { Dialog } from '../components/ui/Dialog/Dialog';
import { NewSurveyForm } from '../components/lighting/NewSurveyForm';
import { formatSurveyDate, listLightSurveys, lightSurveysKey, type LightSurveySummary } from '../services/lightSurveyService';
import { PlacementAssistant } from '../components/flow/PlacementAssistant';
import '../components/lighting/Lighting.css';

function SurveyCard({ survey, aquariumId }: { survey: LightSurveySummary; aquariumId: string }) {
  const { t, i18n } = useTranslation();
  return (
    <li>
      <Link className="lighting__survey-link" to={`/aquariums/${encodeURIComponent(aquariumId)}/lighting/${encodeURIComponent(survey.id)}`}>
        <Card>
          <Stack gap="3">
            {survey.imageUrl ? (
              <img className="lighting__thumb" src={survey.imageUrl} alt="" loading="lazy" />
            ) : (
              <span className="lighting__thumb lighting__thumb--empty" aria-hidden="true" />
            )}
            <Heading level={3}>{survey.title}</Heading>
            <Text color="secondary" size="sm">
              {formatSurveyDate(survey.measuredAt, i18n.language || 'de')}
              {survey.intensityPercent !== null && ` · ${String(t('lighting.meta.intensity', { value: survey.intensityPercent }))}`}
            </Text>
            <Text size="sm">{t('lighting.list.counts', { points: survey.pointCount, corals: survey.coralCount })}</Text>
          </Stack>
        </Card>
      </Link>
    </li>
  );
}

/**
 * Beleuchtung / lighting: the aquarium's PAR surveys ("Lichtkarten") — a photo
 * with PAR values measured at points, and whether the corals on it get the
 * light they need. Starting a survey; how to measure.
 */
export function LightingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const [open, setOpen] = useState(false);
  const query = useQuery({ queryKey: lightSurveysKey(aquariumId), queryFn: () => listLightSurveys(aquariumId), enabled: Boolean(aquariumId) });
  const surveys = query.data ?? [];

  let body;
  if (query.isPending) {
    body = <LiveRegion busy><Text color="muted">{t('lighting.loading')}</Text></LiveRegion>;
  } else if (query.isError) {
    body = <LoadErrorState title={t('lighting.loadError')} onRetry={() => void query.refetch()} retryLabel={t('lighting.retry')} titleLevel={3} />;
  } else if (surveys.length === 0) {
    body = (
      <EmptyState
        icon={<Sun aria-hidden="true" />}
        headline={t('lighting.empty.title')}
        description={t('lighting.empty.description')}
        actionLabel={t('lighting.new.open')}
        actionVariant="secondary"
        onAction={() => setOpen(true)}
        headingLevel={3}
      />
    );
  } else {
    body = (
      <Grid as="ul" minItemWidth="sm" gap="4" className="lighting__grid" data-testid="light-surveys">
        {surveys.map((s) => <SurveyCard key={s.id} survey={s} aquariumId={aquariumId} />)}
      </Grid>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default">
          <PageHeader title={t('lighting.title')} description={t('lighting.subtitle')} actions={<Button onClick={() => setOpen(true)}>{t('lighting.new.open')}</Button>} />
          <Section title={t('lighting.section.surveys')}>{body}</Section>
          {/* Uses the light and the flow map together — offered on both pages (usability audit §4.3). */}
          <Section title={t('flow.placement.title')} description={t('flow.placement.description')}>
            <PlacementAssistant aquariumId={aquariumId} />
          </Section>
          <Section title={t('lighting.howTo.title')}>
            <Card>
              <Stack gap="2">
                <Text>{t('lighting.howTo.intro')}</Text>
                <ul className="lighting__tips">
                  {(['sensor', 'peak', 'height', 'grid', 'repeat'] as const).map((k) => (
                    <li key={k}><Text>{String(t(`lighting.howTo.tips.${k}`))}</Text></li>
                  ))}
                </ul>
                <Text color="muted" variant="caption">{t('lighting.howTo.noLux')}</Text>
              </Stack>
            </Card>
          </Section>
          <Dialog isOpen={open} onClose={() => setOpen(false)} title={t('lighting.new.dialogTitle')} size="md" data-testid="new-light-survey-dialog">
            {open && (
              <NewSurveyForm
                aquariumId={aquariumId}
                onCancel={() => setOpen(false)}
                onCreated={(survey) => {
                  setOpen(false);
                  void query.refetch();
                  navigate(`/aquariums/${encodeURIComponent(aquariumId)}/lighting/${encodeURIComponent(survey.id)}`);
                }}
              />
            )}
          </Dialog>
        </PageContent>
      }
    />
  );
}

export default LightingPage;
